/* Where the public API lives, and which site we are addressing.

   The API is addressed as `{siteToken}.api.platform.<stage-host>/v1`, so the
   site token is part of the hostname rather than a header. We resolve it from
   the request Host when the app is served on `<siteToken>.localhost:3000`,
   which mirrors production addressing and lets one dev server serve any site.
   Plain `localhost` carries no token, so SITE_TOKEN is the fallback. */

const SITE_TOKEN_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/* Chat reads `SITE_TOKEN` for the same value, so both names are accepted
   rather than making a deployment set the token twice under two names. */
const FALLBACK_SITE_TOKEN =
  process.env.PLATFORM_SITE_TOKEN || process.env.SITE_TOKEN || "";

export const siteTokenFromHost = (host: string | null) => {
  if (!host) {
    return FALLBACK_SITE_TOKEN;
  }

  const label = host.split(":").at(0)?.split(".").at(0)?.toLowerCase() ?? "";

  if (SITE_TOKEN_PATTERN.test(label)) {
    return label;
  }

  return FALLBACK_SITE_TOKEN;
};

export const apiBaseUrl = (siteToken: string) => {
  /* A fixed base URL (single-tenant API) takes precedence over the
     multi-tenant `{siteToken}.{apiHost}` scheme. When set, the site token is
     not part of the hostname and is ignored for addressing. */
  const fixed = process.env.PLATFORM_API_BASE_URL?.trim();

  if (fixed) {
    return fixed.replace(/\/+$/, "");
  }

  if (!siteToken) {
    throw new Error(
      "No site token. Serve the app on https://<siteToken>.localhost:3000, or set SITE_TOKEN.",
    );
  }

  const apiHost = process.env.PLATFORM_API_HOST;

  if (!apiHost) {
    throw new Error("No platform API host. Set PLATFORM_API_HOST.");
  }

  return `https://${siteToken}.${apiHost}/v1`;
};
